import { useState } from 'react'
import { useSearchParams } from 'libs/router'
import { useTranslation } from 'libs/i18n'
import { SelectClickTypes } from '@table-library/react-table-library/select'
import {
	ActionsSelect,
	CardWrapper,
	ConfirmModal,
	EmptyTable,
	FormatModal,
	Header,
	SortSelect
} from 'shared/components'
import { useFilter, useModal, useSearch, useTableSelect } from 'shared/hooks'
import { IOption } from 'interfaces/select.interface'
import { PayoutEditDialog } from 'features/payouts/organisms/PayoutEditDialog/PayoutEditDialog'
import type { PayoutItem } from 'features/payouts/types/payouts.types'
import { useFetchPayouts } from './queries/useFetchPayouts'
import { useActionFilters } from './hooks/useActionFilters'
import { useContextMenuOptions } from './hooks/useContextMenuOptions'
import { useSortingFilters } from './hooks/useSortingFilters'
import { useExportPayoutsMutation } from './mutations/useExportPayoutsMutation'
import { useMarkAsPaidMutation } from './mutations/useMarkAsPaidMutation'
import { FiltersSection } from './organisms/FiltersSection/FiltersSection'
import { TableSection } from './organisms/TableSection'

const PayoutsPage = () => {
	const { t } = useTranslation()
	const [searchParams] = useSearchParams()
	const { handleSearch } = useSearch({ name: 'search' })
	const { handleFilter } = useFilter()
	const [checkedPayouts, setCheckedPayouts] = useState<number[]>([])
	const [selectedPayout, setSelectedPayout] = useState<PayoutItem | null>(null)
	const [payoutToPay, setPayoutToPay] = useState<PayoutItem | null>(null)
	const [chosenExportFormat, setChosenExportFormat] = useState<number | string | null>(null)
	const { payouts, isPayoutsLoading } = useFetchPayouts()
	const { select } = useTableSelect({ nodes: payouts?.data?.results ?? [] }, onSelectChange, {
		clickType: SelectClickTypes.ButtonClick
	})
	const clearSelection = () => select.fns.onRemoveAll()
	const sortingFilters = useSortingFilters()
	const [actionsFilter] = useActionFilters(!checkedPayouts.length)
	const menuOptions = useContextMenuOptions({
		onEdit: setSelectedPayout,
		onMarkAsPaid: setPayoutToPay
	})

	const {
		isOpen: isExportModalOpen,
		openModal: openExportModal,
		closeModal: closeExportModal
	} = useModal()
	const {
		isOpen: isFormatModalOpen,
		openModal: openFormatModal,
		closeModal: closeFormatModal
	} = useModal()
	const {
		isOpen: isBulkStatusModalOpen,
		openModal: openBulkStatusModal,
		closeModal: closeBulkStatusModal
	} = useModal()

	const { exportPayoutsMutation, isExportPayoutsLoading } = useExportPayoutsMutation({
		chosenExportFormat,
		checkedPayouts,
		clearSelection,
		onSettled: () => {
			closeFormatModal()
			setChosenExportFormat(null)
		}
	})

	const { markAsPaidMutation } = useMarkAsPaidMutation({
		clearSelection,
		onSettled: () => {
			closeBulkStatusModal()
			setPayoutToPay(null)
		}
	})

	const handleAction = (action: string) => {
		switch (action) {
			case 'export':
				openExportModal()
				break
			case 'bulkStatus':
				openBulkStatusModal()
				break
		}
	}

	const handleExportModal = () => {
		closeExportModal()
		openFormatModal()
	}

	const handleFormatChange = (data: IOption) => {
		data?.value?.toString() ? setChosenExportFormat(data.value) : setChosenExportFormat(null)
	}

	function onSelectChange(action: any, state: any) {
		setCheckedPayouts(state.ids)
	}

	const renderContent = () => {
		if (isPayoutsLoading) return <EmptyTable isLoading />
		if (!payouts?.data?.results?.length)
			return <EmptyTable text={searchParams.toString() ? t('No results') : t('No payouts yet')} />

		return (
			<TableSection
				payouts={payouts}
				select={select}
				menuOptions={menuOptions}
				onOpen={setSelectedPayout}
			/>
		)
	}

	return (
		<>
			<Header
				title={t('Payouts')}
				actions={<SortSelect {...sortingFilters} onChange={handleFilter} />}
			/>
			<div className="mx-auto max-w-[1536px] px-5 lg:flex lg:max-h-[calc(100vh-72px-4rem)] lg:flex-col lg:px-0">
				<CardWrapper className="lg:flex lg:min-h-0 lg:flex-1 lg:flex-col">
					<FiltersSection
						actions={
							<ActionsSelect
								variant="filters"
								options={actionsFilter.options}
								onChange={handleAction}
							/>
						}
						handleSearch={handleSearch}
						handleFilter={handleFilter}
					/>
					{renderContent()}
				</CardWrapper>
			</div>

			<PayoutEditDialog payout={selectedPayout} closeModal={() => setSelectedPayout(null)} />

			{isExportModalOpen && (
				<ConfirmModal
					title={t('Export payouts')}
					isOpened={isExportModalOpen}
					closeModal={closeExportModal}
					confirm={handleExportModal}>
					<p>{t('You are about to export multiple payouts. Are you sure you want to proceed?')}</p>
				</ConfirmModal>
			)}
			{isBulkStatusModalOpen && (
				<ConfirmModal
					title={t('Change status to paid')}
					isOpened={isBulkStatusModalOpen}
					closeModal={closeBulkStatusModal}
					confirm={() => markAsPaidMutation(checkedPayouts)}>
					<p>
						{t(
							'You are about to change the status to paid of multiple payouts. Are you sure you want to proceed?'
						)}
					</p>
				</ConfirmModal>
			)}
			{payoutToPay && (
				<ConfirmModal
					title={t('Change status to paid')}
					isOpened
					closeModal={() => setPayoutToPay(null)}
					confirm={() => markAsPaidMutation([payoutToPay.id])}>
					<p>
						{t(
							'You are about to mark payout #{{ id }} as paid. Are you sure you want to proceed?',
							{
								id: payoutToPay.reference
							}
						)}
					</p>
				</ConfirmModal>
			)}
			{isFormatModalOpen && (
				<FormatModal
					disableButton={chosenExportFormat === null}
					isOpened={isFormatModalOpen}
					closeModal={closeFormatModal}
					confirm={() => exportPayoutsMutation()}
					isLoading={isExportPayoutsLoading}
					onChange={handleFormatChange}
				/>
			)}
		</>
	)
}

export default PayoutsPage
